// Hand-built track presets. Each cell is [ gx, gz, pieceName, godotOrient ]
// where godotOrient maps to ORIENT_DEG in Track.js: 0=0° 10=180° 16=90° 22=270°.
//
// The art and collider set only supports single-tile straights/corners, so the
// "F1-inspired" feel here comes from broader overall layouts, longer sectors,
// and more direction changes rather than true multi-radius corners.

import { CELL_RAW, GRID_SCALE, TRACK_CELLS } from './Track.js';

const STRAIGHT_ORIENT = {
	'1,0': 16,
	'-1,0': 22,
	'0,1': 0,
	'0,-1': 10,
};

const CORNER_ORIENT = {
	'0,1|1,0': 16,
	'-1,0|0,1': 0,
	'-1,0|0,-1': 22,
	'0,-1|1,0': 10,
};

function dirKey( dx, dz ) {

	return `${dx},${dz}`;

}

function connectionKey( a, b ) {

	return [ dirKey( a[ 0 ], a[ 1 ] ), dirKey( b[ 0 ], b[ 1 ] ) ].sort().join( '|' );

}

function expandLoopPath( points ) {

	if ( points.length < 4 ) throw new Error( 'Track loop needs at least four points' );

	const start = points[ 0 ];
	const cells = [ [ start[ 0 ], start[ 1 ] ] ];

	for ( let i = 0; i < points.length; i ++ ) {

		const from = points[ i ];
		const to = points[ ( i + 1 ) % points.length ];
		const dx = Math.sign( to[ 0 ] - from[ 0 ] );
		const dz = Math.sign( to[ 1 ] - from[ 1 ] );

		if ( ( dx === 0 ) === ( dz === 0 ) ) {

			throw new Error( `Track segment ${ i } must be non-zero and axis-aligned` );

		}

		let x = from[ 0 ];
		let z = from[ 1 ];
		while ( true ) {

			if ( x === to[ 0 ] && z === to[ 1 ] ) break;
			x += dx;
			z += dz;

			if ( i === points.length - 1 && x === start[ 0 ] && z === start[ 1 ] ) break;
			cells.push( [ x, z ] );

		}

	}

	return cells;

}

function buildLoop( points, finishCell ) {

	const path = expandLoopPath( points );
	const used = new Set();
	const finishKey = finishCell ? `${ finishCell[ 0 ] },${ finishCell[ 1 ] }` : null;

	for ( const [ gx, gz ] of path ) {

		const key = `${ gx },${ gz }`;
		if ( used.has( key ) ) throw new Error( `Track loop overlaps itself at ${ key }` );
		used.add( key );

	}

	return path.map( ( [ gx, gz ], index ) => {

		const prev = path[ ( index - 1 + path.length ) % path.length ];
		const next = path[ ( index + 1 ) % path.length ];
		const prevDir = [ prev[ 0 ] - gx, prev[ 1 ] - gz ];
		const nextDir = [ next[ 0 ] - gx, next[ 1 ] - gz ];
		const isStraight = prevDir[ 0 ] === - nextDir[ 0 ] && prevDir[ 1 ] === - nextDir[ 1 ];

		if ( isStraight ) {

			const orient = STRAIGHT_ORIENT[ dirKey( nextDir[ 0 ], nextDir[ 1 ] ) ];
			const name = finishKey === `${ gx },${ gz }` ? 'track-finish' : 'track-straight';
			return [ gx, gz, name, orient ];

		}

		const orient = CORNER_ORIENT[ connectionKey( prevDir, nextDir ) ];
		if ( orient === undefined ) throw new Error( `Unsupported corner at ${ gx },${ gz }` );
		if ( finishKey === `${ gx },${ gz }` ) throw new Error( 'Finish cell must be on a straight' );
		return [ gx, gz, 'track-corner', orient ];

	} );

}

function loopLength( cells ) {

	return Math.round( cells.length * CELL_RAW * GRID_SCALE );

}

function withPieceOverrides( cells, overrides ) {

	const overrideMap = new Map(
		overrides.map( ( [ gx, gz, type ] ) => [ `${ gx },${ gz }`, type ] )
	);

	return cells.map( ( cell ) => {

		const [ gx, gz, type, orient ] = cell;
		return [ gx, gz, overrideMap.get( `${ gx },${ gz }` ) || type, orient ];

	} );

}

const APEX = buildLoop( [
	[ -7, -4 ],
	[ 3, -4 ],
	[ 3, -1 ],
	[ 1, -1 ],
	[ 1, 2 ],
	[ 6, 2 ],
	[ 6, 7 ],
	[ -2, 7 ],
	[ -2, 4 ],
	[ -5, 4 ],
	[ -5, 1 ],
	[ -7, 1 ],
], [ 3, -3 ] );

const COLISEUM = buildLoop( [
	[ -8, -6 ],
	[ -1, -6 ],
	[ -1, -3 ],
	[ 5, -3 ],
	[ 5, 1 ],
	[ 2, 1 ],
	[ 2, 6 ],
	[ -6, 6 ],
	[ -6, 2 ],
	[ -3, 2 ],
	[ -3, -1 ],
	[ -8, -1 ],
], [ 5, -1 ] );

const LE_GRAND = buildLoop( [
	[ -10, -7 ],
	[ 2, -7 ],
	[ 2, -4 ],
	[ 7, -4 ],
	[ 7, 0 ],
	[ 4, 0 ],
	[ 4, 4 ],
	[ 9, 4 ],
	[ 9, 9 ],
	[ -4, 9 ],
	[ -4, 5 ],
	[ -8, 5 ],
	[ -8, 1 ],
	[ -10, 1 ],
], [ 7, -2 ] );

const MEGA_BOWL = buildLoop( [
	[ -13, -9 ],
	[ 2, -9 ],
	[ 2, -6 ],
	[ 10, -6 ],
	[ 10, -1 ],
	[ 6, -1 ],
	[ 6, 4 ],
	[ 13, 4 ],
	[ 13, 12 ],
	[ -3, 12 ],
	[ -3, 7 ],
	[ -9, 7 ],
	[ -9, 2 ],
	[ -13, 2 ],
], [ 10, -4 ] );

const SKYWAY_LOOP = buildLoop( [
	[ -11, -6 ],
	[ 2, -6 ],
	[ 2, -2 ],
	[ 8, -2 ],
	[ 8, 4 ],
	[ 3, 4 ],
	[ 3, 9 ],
	[ -8, 9 ],
	[ -8, 3 ],
	[ -11, 3 ],
], [ 1, -6 ] );

const SKYWAY = withPieceOverrides( SKYWAY_LOOP, [
	[ -1, -6, 'track-bump' ],
	[ 0, -6, 'track-bump' ],
	[ 1, -6, 'track-bump' ],
	[ 8, 1, 'track-bump' ],
	[ 8, 2, 'track-bump' ],
	[ -6, 9, 'track-bump' ],
	[ -5, 9, 'track-bump' ],
] );

const CANAL_RUSH_LOOP = buildLoop( [
	[ -12, -8 ],
	[ -2, -8 ],
	[ -2, -4 ],
	[ 4, -4 ],
	[ 4, 0 ],
	[ 10, 0 ],
	[ 10, 7 ],
	[ 2, 7 ],
	[ 2, 11 ],
	[ -10, 11 ],
	[ -10, 4 ],
	[ -12, 4 ],
], [ -4, -8 ] );

const CANAL_RUSH = withPieceOverrides( CANAL_RUSH_LOOP, [
	[ -1, -8, 'track-bump' ],
	[ 0, -8, 'track-bump' ],
	[ 1, -8, 'track-bump' ],
	[ 10, 3, 'track-bump' ],
	[ 10, 4, 'track-bump' ],
	[ -8, 11, 'track-bump' ],
	[ -7, 11, 'track-bump' ],
] );

export const PRESET_TRACKS = [
	{
		id: 'default',
		name: 'Compact Circuit',
		hint: 'classic loop · best for solo · 80 m',
		cells: TRACK_CELLS,
	},
	{
		id: 'apex',
		name: 'Apex Speedway',
		hint: `fast sector one · hard-stop hairpin · ${ loopLength( APEX ) } m`,
		cells: APEX,
	},
	{
		id: 'coliseum',
		name: 'Coliseum',
		hint: `stadium esses · balanced GP loop · ${ loopLength( COLISEUM ) } m`,
		cells: COLISEUM,
	},
	{
		id: 'le-grand',
		name: 'Le Grand',
		hint: `sweeping middle sector · long back straight · ${ loopLength( LE_GRAND ) } m`,
		cells: LE_GRAND,
	},
	{
		id: 'mega-bowl',
		name: 'Mega Bowl',
		hint: `wide multiplayer GP · room for 8+ racers · ${ loopLength( MEGA_BOWL ) } m`,
		cells: MEGA_BOWL,
	},
	{
		id: 'skyway',
		name: 'Skyway Rush',
		hint: `bridge launches · neon tunnel · ${ loopLength( SKYWAY ) } m`,
		cells: SKYWAY,
		setpieces: [
			{ type: 'bridge-waterway', from: [ -2, -6 ], to: [ 2, -6 ], waterWidth: 2.7, color: 0x2fd6ff },
			{ type: 'neon-tunnel', from: [ 8, -1 ], to: [ 8, 4 ], color: 0xff4fd8 },
		],
	},
	{
		id: 'canal-rush',
		name: 'Canal Rush',
		hint: `triple jump causeway · river edge sprint · ${ loopLength( CANAL_RUSH ) } m`,
		cells: CANAL_RUSH,
		setpieces: [
			{ type: 'bridge-waterway', from: [ -2, -8 ], to: [ 2, -8 ], waterWidth: 3.1, color: 0x3df0ff },
			{ type: 'bridge-waterway', from: [ 10, 1 ], to: [ 10, 5 ], waterWidth: 2.5, color: 0x1fb7ff },
			{ type: 'neon-tunnel', from: [ -9, 11 ], to: [ -6, 11 ], color: 0x7f67ff },
		],
	},
];

export function getPreset( id ) {

	return PRESET_TRACKS.find( ( t ) => t.id === id ) || PRESET_TRACKS[ 0 ];

}
